/**
 * Reading the Portfolio's rows — ROADMAP 6.9, repointed onto the Trade grain
 * by 7.10.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * READS THE PROJECTION, NOT THE BROKER'S BOOK
 *
 * `/market/positions` is the broker's own book, and it is what the EXIT path
 * sizes against — the only source that can say what an account actually
 * holds right now. Every screen reads the projection instead: this one
 * through `/portfolio/positions` and the terminal drawer through
 * `/trades/open`, both of them `TradeLeg` rows folded from our own fills,
 * because that is the only source carrying REALISED P&L, charges, and which
 * basket a leg belongs to. The broker's book cannot answer "how did this get
 * here"; a fold over fills can.
 *
 * 7.10 ALSO CHANGED THE GRAIN. The rows used to be the BROKER's positions,
 * which net two baskets on one strike into one. They are our legs now, so two
 * baskets are two rows — which is what the user placed.
 *
 * That inversion is 6.1's whole premise: the projection is the truth and the
 * book is a check. This is the first screen that depends on it.
 *
 * A FAILED READ IS NOT AN EMPTY PORTFOLIO
 *
 * Same rule as everywhere else in this project. `UNREADABLE` is its own
 * state, distinct from a portfolio with nothing in it, and the screen says
 * which it is looking at.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { api, qs } from '@/lib/api'
import { useTickLtps, useTickSubscription } from '@/feed/useTick'
import { toRow, type PositionRow, type ProjectedPosition } from './portfolioModel'

export type PortfolioState =
  | { readonly kind: 'IDLE' }
  | { readonly kind: 'LOADING' }
  | { readonly kind: 'UNREADABLE'; readonly why: string }
  | { readonly kind: 'READY'; readonly rows: readonly PositionRow[] }

/** Validate at the boundary: `api.get<T>` is an assertion, not a check. */
function asPositions(value: unknown): ProjectedPosition[] | null {
  if (typeof value !== 'object' || value === null) return null
  const items = (value as { items?: unknown }).items
  if (!Array.isArray(items)) return null
  return items.filter(
    (i): i is ProjectedPosition =>
      typeof i === 'object' &&
      i !== null &&
      typeof (i as ProjectedPosition).tradeLegId === 'string' &&
      typeof (i as ProjectedPosition).netQty === 'number' &&
      typeof (i as ProjectedPosition).avgPrice === 'number',
  )
}

export function usePortfolio(
  connectionId: string | null,
): PortfolioState & { readonly refresh: () => void; readonly ltpOf: (t: number | null) => number | null } {
  const [items, setItems] = useState<ProjectedPosition[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)
  const [nonce, setNonce] = useState(0)

  useEffect(() => {
    if (!connectionId) {
      setItems(null)
      setError(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    void api
      .get<unknown>(`/portfolio/positions${qs({ connectionId })}`)
      .then((r) => {
        if (cancelled) return
        const parsed = asPositions(r)
        if (parsed === null) {
          setError('the portfolio came back in a shape this build does not understand')
          setItems(null)
          return
        }
        setItems(parsed)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        // A failed request is not an empty portfolio.
        setError(e instanceof Error ? e.message : 'could not read the portfolio')
        setItems(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [connectionId, nonce])

  /*
   * The projection lists positions the chain may not have subscribed — a leg
   * opened last week on an expiry nobody is looking at today. Demand them, or
   * every carried row sits unmarked forever.
   */
  const tokens = useMemo(
    () => (items ?? []).map((i) => i.token).filter((t): t is number => t !== null && t > 0),
    [items],
  )
  useTickSubscription(tokens)
  const ltps = useTickLtps(tokens)

  const ltpOf = useCallback(
    (token: number | null): number | null => (token === null ? null : (ltps.get(token) ?? null)),
    [ltps],
  )
  const refresh = useCallback(() => setNonce((n) => n + 1), [])

  const state = useMemo((): PortfolioState => {
    if (!connectionId) return { kind: 'IDLE' }
    if (error !== null) return { kind: 'UNREADABLE', why: error }
    if (items === null) return loading ? { kind: 'LOADING' } : { kind: 'IDLE' }
    return { kind: 'READY', rows: items.map((i) => toRow(i, ltps.get(i.token ?? -1))) }
  }, [connectionId, error, items, loading, ltps])

  return { ...state, refresh, ltpOf }
}
